import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook para detectar quando o usuário está inativo por um tempo determinado
 * e fazer logout automático
 * 
 * @param {object} currentUser - Usuário atual, se existir
 * @param {number} idleTime - Tempo em milissegundos para considerar o usuário inativo (padrão: 10 minutos)
 * @returns {boolean} - Estado de atividade do usuário
 */
const useIdleTimeout = (currentUser, idleTime = 600000) => {
  const [isIdle, setIsIdle] = useState(false);
  const { logout } = useAuth();

  const handleUserActivity = useCallback(() => {
    setIsIdle(false);
    resetTimer();
  }, []);

  const handleTimeout = useCallback(() => {
    // Só executa o logout se o usuário estiver logado
    if (currentUser) {
      setIsIdle(true);
      logout();
      // Redirecionar para a página de login da aplicação em vez do site externo
      window.location.href = '/login';
    }
  }, [logout, currentUser]);

  const resetTimer = useCallback(() => {
    // Limpa o timeout anterior se existir
    if (window.timeoutId) {
      clearTimeout(window.timeoutId);
    }
    
    // Só configura o timer se o usuário estiver logado
    if (currentUser) {
      window.timeoutId = setTimeout(handleTimeout, idleTime);
    }
  }, [handleTimeout, idleTime, currentUser]);

  useEffect(() => {
    // Só ativa o monitoramento se o usuário estiver logado
    if (!currentUser) return;
    
    // Eventos que reiniciam o timer de inatividade
    const events = [
      'mousemove',
      'mousedown',
      'keypress',
      'touchstart',
      'scroll',
      'click'
    ];

    // Adiciona os listeners de evento
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    // Inicia o timer
    resetTimer();

    // Cleanup ao desmontar o componente
    return () => {
      if (window.timeoutId) {
        clearTimeout(window.timeoutId);
      }
      
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity, resetTimer, currentUser]);

  return isIdle;
};

export default useIdleTimeout; 